import { Box, Button, ButtonProps, Typography } from "@outschool/backpack";
import {
  AccountSectionNewQuery,
  StopSubscriptionMutation,
  StopSubscriptionMutationVariables,
} from "@outschool/gql-frontend-generated";
import { renderCurrencyString, useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import { useMutation } from "@outschool/ui-apollo";
import { useTopNoticeContext } from "@outschool/ui-components-website";
import _ from "lodash";
import React, { FormEvent, useCallback, useRef, useState } from "react";

import * as Env from "../../../shared/Env";
import {
  EnrollmentListQuery,
  getEnrollmentListQueryVariables,
} from "../../queries/EnrollmentListQuery";
import { stopSubscriptionMutation } from "../../queries/EnrollmentMutations";
import ActivitySummary from "../enrolling/ActivitySummary";
import ErrorMessage from "../ErrorMessage";
import PageModal from "../PageModal";
import { enrolledClassesQuery } from "../parentDashboardV2/queries/enrolledClassesQuery";
import {
  EnrolledClassActivity,
  EnrolledClassEnrollment,
  EnrolledClassGiftEnrollment,
  EnrolledClassPayments,
  EnrolledClassSection,
} from "./EnrolledClassTypes";
import StopSubscriptionReasonForm from "./StopSubscriptionReasonForm";

export default React.forwardRef(StopSubscription);

function StopSubscription(
  {
    buttonLabel,
    activity,
    section,
    studentName,
    userTimeZone,
    enrollment,
    isTeacher,
    onClick,
    ...buttonProps
  }: StopSubscriptionProps,
  ref: React.Ref<any>,
) {
  const refundablePayments = enrollment.payments.filter(
    ep => ep.isOngoingRefundable,
  );

  return (
    <StopSubscriptionButtonModal
      ref={ref}
      buttonLabel={buttonLabel}
      activity={activity}
      section={section}
      enrollment={enrollment}
      refundPayments={refundablePayments}
      userTimeZone={userTimeZone}
      studentName={studentName}
      isTeacher={isTeacher}
      onClick={onClick}
      {...buttonProps}
    />
  );
}

type StopSubscriptionProps = ButtonProps & {
  buttonLabel: string;
  activity:
    | EnrolledClassActivity
    | NonNullable<AccountSectionNewQuery["section"]>["activity"];
  section: EnrolledClassSection | AccountSectionNewQuery["section"];
  enrollment:
    | EnrolledClassEnrollment
    | EnrolledClassGiftEnrollment
    | NonNullable<
        NonNullable<AccountSectionNewQuery["section"]>["selectedEnrollment"]
      >;
  refundPayments?: EnrolledClassPayments;
  userTimeZone?: string | undefined;
  studentName: string;
  isTeacher?: boolean | undefined;
};

function _StopSubscriptionButtonModal(
  {
    buttonLabel,
    activity,
    section,
    enrollment,
    refundPayments,
    userTimeZone,
    studentName,
    isTeacher,
    onClick,
    ...buttonProps
  }: StopSubscriptionProps,
  ref: React.Ref<any>,
) {
  const reasonInput = useRef();
  const noteInput = useRef();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");

  const [stopSubscription, { loading: mutationInProgress }] = useMutation<
    StopSubscriptionMutation,
    StopSubscriptionMutationVariables
  >(stopSubscriptionMutation, {
    refetchQueries: [
      {
        query: EnrollmentListQuery,
        // @ts-ignore TS(2531): Object is possibly 'null'.
        variables: getEnrollmentListQueryVariables(section.uid),
      },
      enrolledClassesQuery,
    ],
  });

  const [error, setError] = useState(null);
  const { setTopNotice } = useTopNoticeContext();
  const { t } = useTranslation("client\\components\\account\\StopSubscription");

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onClick?.(event);
      setOpen(true);
    },
    [onClick],
  );
  const handleClose = useCallback(() => setOpen(false), []);
  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      try {
        await stopSubscription({
          variables: {
            enrollmentUid: enrollment.uid,
            reason,
            note,
            // TODO: Technical debt
            // This assumes that there is one payment per subscription period
            // Change this to a time range
            // See discussion:
            // https://github.com/Outschool/Outschool/pull/3374#discussion_r279025687
            refundPaymentUids: refundPayments?.map(rp => rp.uid),
          },
        });
        setTopNotice(
          `Stopped subscription for ${studentName} to ${activity.title}`,
        );
        setError(null);
      } catch (error) {
        setError(error);
      }
    },
    [
      reason,
      note,
      enrollment.uid,
      studentName,
      activity.title,
      refundPayments,
      stopSubscription,
      setTopNotice,
    ],
  );

  const payments = enrollment.payments;
  const lastEnrollmentPayment = _.maxBy(
    refundPayments
      ? payments.filter(p => !refundPayments.find(rp => p.uid === rp.uid))
      : payments,
    "end_time",
  );
  const enrolledThrough = lastEnrollmentPayment?.end_time;

  const refundCreditAmount = refundPayments
    ? _.sumBy(refundPayments, "unrefundedCredits")
    : 0;
  const refundAmount = refundPayments
    ? _.sumBy(refundPayments, "unrefundedCents")
    : 0;

  let refundMessage = "";
  if (refundCreditAmount > 0) {
    if (isTeacher) {
      refundMessage = t(
        "Parent will have {{numCredits}} credits returned to their account.",
        {
          numCredits: refundCreditAmount,
        },
      );
    } else {
      refundMessage = t(
        "You will have {{numCredits}} credits returned to your account.",
        {
          numCredits: refundCreditAmount,
        },
      );
    }
  } else if (refundAmount > 0) {
    if (isTeacher) {
      refundMessage = t("Parent will be refunded {{classPrice}}.", {
        classPrice: renderCurrencyString({
          priceInCents: refundAmount,
        }),
      });
    } else {
      refundMessage = t("You will be refunded {{classPrice}}.", {
        classPrice: renderCurrencyString({
          priceInCents: refundAmount,
        }),
      });
    }
  } else {
    refundMessage = t("This will stop future payments.");
  }

  return (
    <React.Fragment>
      <Button ref={ref} variant="link" onClick={handleOpen} {...buttonProps}>
        {buttonLabel}
      </Button>
      <PageModal open={open} onClose={handleClose}>
        <ActivitySummary
          activity={activity}
          startTime={section?.start_time}
          endTime={section?.end_time}
          nextOngoingMeeting={section?.nextOngoingMeeting}
        />
        <Typography
          variant="h3"
          sx={{
            marginY: "0.5em",
          }}
        >
          {t("Stop subscription for {{studentName}} to this class", {
            studentName,
          })}
        </Typography>
        <ErrorMessage
          value={error}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
        <Box
          sx={{
            marginBottom: "1em",
          }}
          data-test-id="explanation-details"
        >
          {refundMessage}{" "}
          {t(
            "{{studentName}} will no longer have access to this class or recordings after {{enrollmentEndTime}}.",
            {
              studentName,
              enrollmentEndTime: Time.formatDate(enrolledThrough, userTimeZone),
            },
          )}
        </Box>
        <StopSubscriptionReasonForm
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          onReasonDropdownChange={value => {
            setReason(value);
            setError(null);
          }}
          onReasonNoteChange={e => {
            setNote(e.target.value);
            setError(null);
          }}
          reason={reason}
          note={note}
          // @ts-ignore TS(2322): Type 'MutableRefObject<undefined>' is not assignab... Remove this comment to see the full error message
          reasonInputRef={reasonInput}
          // @ts-ignore TS(2322): Type 'MutableRefObject<undefined>' is not assignab... Remove this comment to see the full error message
          noteInputRef={noteInput}
          mutationInProgress={mutationInProgress}
        />
      </PageModal>
    </React.Fragment>
  );
}

const StopSubscriptionButtonModal = React.forwardRef(
  _StopSubscriptionButtonModal,
);
